import { FunctionComponent } from 'preact';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'preact/hooks';
import { playerDataSelector } from '../../../common/store/selectors/playerData.selector';
import { useStyles } from '../../../common/hooks/useStyles';
import { getReadMoreStyleObject } from './readMore.style';
import { isEmpty } from '../../../common/utils/utils';
import { getButtonId, getButtonTextId } from '../htmlElements/htmlElementsGetter';
import { adsDataSelector } from '../../../common/store/selectors/adsData.selector';
import { css } from '../../../common/styles/styles.utils';
import { setUserInteraction } from '../store/readMore.actions';
import { useHoverRef } from '../../../common/hooks/useHoverRef';
import { ErrorBoundary } from '../../../common/components/errorBoundary/errorBoundary';
import { recirculationVideoDataSelector } from '../../../common/store/selectors/recirculationVideoData.selector';
import { RecirculationCustomParams } from '../index';
import { SelectedSceneData } from '../../../common/store/store.types';
import { anchorDataSelector } from '../../../common/store/selectors/anchorData.selector';
import { COMMON_ICON_TYPES } from '../../../common/components/icon/iconMapper';
import { Icon } from '../../../common/components/icon/icon';
import { setPosition, setRecirculationData } from '../../../core/store/playerData/playerDataSlice';

const READ_MORE_PLUGIN = 'ReadMore';
const ARIA_LABEL = 'Read more';

const updateCurrentSceneOnVideoProgress = (currentScene: number, sortedCustomParams: RecirculationCustomParams, dispatch: any): void => {
  const startOfCurrentScene = currentScene === -1 ? 0 : sortedCustomParams[currentScene].endOfScene;
  const endOfCurrentScene = sortedCustomParams[currentScene + 1].endOfScene;
  dispatch(setRecirculationData({ currentScene: currentScene + 1, startOfCurrentScene, endOfCurrentScene }));
};

const updateCurrentSceneOnSeek = (selectedScene: SelectedSceneData, sortedCustomParams: RecirculationCustomParams, setPositionAfterSeek: (position: number) => void, dispatch: any) => {
  const { index, progressPercentage } = selectedScene;
  const startOfSelectedScene = index === 0 ? 0 : sortedCustomParams[index - 1].endOfScene;
  const endOfSelectedScene = sortedCustomParams[index].endOfScene;
  const storyDuration = endOfSelectedScene - startOfSelectedScene;
  const newPosition = startOfSelectedScene + (progressPercentage * storyDuration);
  setPositionAfterSeek(newPosition);
  dispatch(setRecirculationData({ currentScene: index, startOfCurrentScene: startOfSelectedScene, endOfCurrentScene: endOfSelectedScene }));
  dispatch(setPosition(newPosition));
};

interface ReadMoreProps {
  sortedCustomParams: RecirculationCustomParams;
}

export const ReadMore: FunctionComponent<ReadMoreProps> = props => {
  const { sortedCustomParams } = props;
  const dispatch = useDispatch();
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqueId);
  const currentVideoTime = useSelector(playerDataSelector.currentVideoTime);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);
  const adStatus = useSelector(adsDataSelector.adStatus);
  const playerMode = useSelector(playerDataSelector.playerMode);
  const currentScene = useSelector(recirculationVideoDataSelector.currentScene);
  const endOfCurrentScene = useSelector(recirculationVideoDataSelector.endOfCurrentScene);
  const playerSize = useSelector(playerDataSelector.playerSize);
  const selectedScene = useSelector(recirculationVideoDataSelector.selectedScene);
  const anchorStatus = useSelector(anchorDataSelector.anchorStatus);

  const [articleUrl, setArticleUrl] = useState<string>('');
  const [buttonRef, buttonHovered] = useHoverRef();
  const [positionAfterSeek, setPositionAfterSeek] = useState<number>(-1);

  const anchorStatusActive = anchorStatus === 'active';
  const adPlaying = adStatus === 'playing' || adStatus === 'paused';
  const mobilePlayerMode = playerMode === 'mobile' || playerMode === 'miniMobile' || playerMode === 'stickyMobile';
  const shouldAddBlackStrip = (playerSize === 'extraSmall' || playerSize === 'small') && anchorStatusActive;
  const videoCompleted = videoTagStatus === 'unloaded';
  const videoLoading = videoCompleted || videoTagStatus === 'idle';

  const styles = useStyles(getReadMoreStyleObject, [anchorStatusActive, videoLoading, adPlaying, buttonHovered, playerSize, mobilePlayerMode]);
  const { readMoreContainerStyle, buttonWrapperStyle, blackStripStyle, readMoreButtonStyle, readMoreTextStyle, arrowUpStyle } = styles;

  useEffect(() => {
    if (selectedScene.index !== -1) {
      updateCurrentSceneOnSeek(selectedScene, sortedCustomParams, setPositionAfterSeek, dispatch);
    }
  }, [selectedScene, sortedCustomParams, setPositionAfterSeek, dispatch]);

  useEffect(() => {
    if (Math.floor(positionAfterSeek) === currentVideoTime) {
      setPositionAfterSeek(-1);
    } else if (positionAfterSeek === -1 && currentVideoTime >= endOfCurrentScene && currentScene < sortedCustomParams.length - 1) {
      updateCurrentSceneOnVideoProgress(currentScene, sortedCustomParams, dispatch);
    }
  }, [currentVideoTime, endOfCurrentScene, currentScene, sortedCustomParams, positionAfterSeek, setPositionAfterSeek, dispatch]);

  useEffect(() => {
    if (!isEmpty(sortedCustomParams[currentScene])) {
      setArticleUrl(sortedCustomParams[currentScene].sceneUrl);
    }
  }, [currentScene, sortedCustomParams, setArticleUrl]);

  const onReadMoreClicked = (): void => {
    dispatch(setUserInteraction('recirculationArticle'));
    if (!isEmpty(articleUrl)) {
      window.open(articleUrl, '_blank');
    }
  };

  return (
    <ErrorBoundary pluginName={READ_MORE_PLUGIN}>
      <div style={css(readMoreContainerStyle)}>
        { shouldAddBlackStrip && <div style={css(blackStripStyle)} /> }
        <div style={css(buttonWrapperStyle)}>
          <div style={css(readMoreButtonStyle)} onClick={onReadMoreClicked} ref={buttonRef} id={getButtonId(playerUniqueId)} aria-label={ARIA_LABEL}>
            <span style={css(readMoreTextStyle)} id={getButtonTextId(playerUniqueId)}>read more</span>
            {!anchorStatusActive
            && (
            <Icon
              icon={COMMON_ICON_TYPES.ARROW_UP_RIGHT}
              style={css(arrowUpStyle)}
            />
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
